import vocabulary from '../data/verdict-vocabulary.json'
import type { Verdict } from './types'

/**
 * Resolving what a check concluded.
 *
 * The status field is not usable for this. Generators set it to a "completed"
 * value regardless of outcome, because it describes whether the check ran
 * rather than what it found. The conclusion lives in the output string, which
 * carries no type and no agreed vocabulary: one tool writes true/false, another
 * valid/invalid, another secure/insecure.
 *
 * So the mapping lives in a data file. An unrecognised value resolves to
 * unknown and is reported, never guessed at: silently treating it as a failure
 * would misreport software, which is worse than admitting the gap.
 */

type VocabularyFile = {
  pass: string[]
  fail: string[]
  notApplicable: string[]
  toolError: string[]
}

const table = buildTable(vocabulary as VocabularyFile)
const toolErrors = new Set((vocabulary as VocabularyFile).toolError.map(normaliseKey))

function buildTable(source: VocabularyFile): Map<string, Verdict> {
  const map = new Map<string, Verdict>()
  const add = (values: string[], verdict: Verdict) => {
    for (const value of values) map.set(normaliseKey(value), verdict)
  }
  add(source.pass, 'pass')
  add(source.fail, 'fail')
  add(source.notApplicable, 'notApplicable')
  return map
}

function normaliseKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ')
}

/** Whether a check records its tool failing, by status or by output. */
function toolFailed(status: string | undefined, output: string | undefined): boolean {
  if (status && /failedactionstatus/i.test(status)) return true
  return output !== undefined && output !== null && toolErrors.has(normaliseKey(String(output)))
}

export function resolveOutput(output: string | undefined): Verdict {
  if (output === undefined || output === null) return 'unknown'
  return table.get(normaliseKey(String(output))) ?? 'unknown'
}

/**
 * A document may state its own result rather than leaving it to be inferred.
 * Generators compute this internally; when one starts writing it out, it should
 * win over any interpretation of the output string.
 */
export function resolveVerdict(raw: { output?: string; declared?: unknown; status?: string }): {
  verdict: Verdict
  source: 'declared' | 'vocabulary' | 'unresolved' | 'toolFailed'
} {
  // The one thing the status does say: the tool did not complete. Whatever it
  // wrote is then about the tool, not the software, so it does not vote.
  if (toolFailed(raw.status, raw.output)) return { verdict: 'unknown', source: 'toolFailed' }
  if (typeof raw.declared === 'boolean') {
    return { verdict: raw.declared ? 'pass' : 'fail', source: 'declared' }
  }
  const verdict = resolveOutput(raw.output)
  return {
    verdict,
    source: verdict === 'unknown' ? 'unresolved' : 'vocabulary',
  }
}
